/**
 * The top bar: active company, the mini timer slot, locale, theme, who is signed in,
 * and sign out.
 *
 * The timer slot is a labelled region rather than an absent one, which is what let
 * Task 42 (WP-K2) fill it by adding a component instead of changing this layout.
 * `App.tsx` now always passes `<MiniTimer />`, so the fallback below is reached only
 * by a caller that renders `AppShell` without the prop — a test harness, or a future
 * tree that has no `TimerProvider` around it. It says exactly that, and no longer
 * names a task as pending: WP-K2 has run.
 *
 * ── One line on a phone ───────────────────────────────────────────────────────
 *
 * At 390px the six things above stacked into six rows of chrome before a reader got
 * to the page. They are now wrapped in `.wp-topbar__tools`, which is
 * `display: contents` on a desktop — the bar is byte-for-byte the layout it was —
 * and a sheet under the bar, opened by the one button beside the company name,
 * below 900px. Nothing is removed at any width: the collapse is `display` on one
 * container, so every control is still in the DOM for a test and for a screen
 * reader that reaches it through `aria-controls`.
 */

import { useState, type ReactNode } from 'react';

import { useI18n } from '../i18n';
import { LOCALES, type Locale } from '../i18n/messages';
import type { SessionUser } from '../api/types';
import { Icon } from '../states/icons';
import { CompanySwitcher } from './CompanySwitcher';
import { useTheme, type Theme } from './theme';

export interface TopBarProps {
  readonly user: SessionUser;
  readonly onSignOut: () => void;
  /** Task 42 passes the live timer here. */
  readonly timerSlot?: ReactNode;
}

const LOCALE_LABEL: Readonly<Record<Locale, 'locale.vi' | 'locale.en'>> = {
  vi: 'locale.vi',
  en: 'locale.en',
};

const THEMES: readonly Theme[] = ['light', 'dark'];

export function TopBar({ user, onSignOut, timerSlot }: TopBarProps): ReactNode {
  const { t, locale, setLocale } = useI18n();
  const { theme, setTheme } = useTheme();
  const [toolsOpen, setToolsOpen] = useState(false);
  const name = user.display_name ?? user.email ?? user.employee_id;

  return (
    <header className="wp-shell__topbar">
      <CompanySwitcher user={user} />

      {/* Below 900px the bar is one 56px line: the company on the left and this on
          the right. Everything after it collapses into a sheet under the bar. The
          controls stay in the DOM at every width — only their container's `display`
          changes — so a keyboard user tabbing the open sheet and a test querying
          `sign-out` find the same nodes. */}
      <button
        type="button"
        className="wp-button wp-topbar__more"
        aria-expanded={toolsOpen}
        aria-controls="wp-topbar-tools"
        onClick={() => {
          setToolsOpen((open) => !open);
        }}
        data-testid="topbar-more"
      >
        <Icon name="sliders-horizontal" />
        <span className="wp-visually-hidden">{t('shell.topbarMore')}</span>
      </button>

      <div
        className="wp-topbar__tools"
        id="wp-topbar-tools"
        data-open={toolsOpen ? 'true' : 'false'}
      >
        <div className="wp-timer-slot" data-testid="timer-slot">
          <span className="wp-visually-hidden">{t('shell.timerSlot')}: </span>
          {timerSlot ?? t('shell.timerSlotEmpty')}
        </div>

        <div className="wp-shell__spacer" />

        <div className="wp-toggle-group" role="group" aria-label={t('locale.label')}>
          {LOCALES.map((candidate) => (
            <button
              key={candidate}
              type="button"
              className="wp-toggle"
              aria-pressed={locale === candidate}
              onClick={() => {
                setLocale(candidate);
              }}
              data-testid={`locale-${candidate}`}
            >
              {t(LOCALE_LABEL[candidate])}
            </button>
          ))}
        </div>

        <div className="wp-toggle-group" role="group" aria-label={t('theme.label')}>
          {THEMES.map((candidate) => (
            <button
              key={candidate}
              type="button"
              className="wp-toggle"
              aria-pressed={theme === candidate}
              onClick={() => {
                setTheme(candidate);
              }}
              data-testid={`theme-${candidate}`}
            >
              {candidate === 'light' ? t('theme.light') : t('theme.dark')}
            </button>
          ))}
        </div>

        <div className="wp-user">
          <span data-testid="user-name">
            <span className="wp-visually-hidden">{t('shell.signedInAs', { name })}</span>
            <span aria-hidden="true">{name}</span>
          </span>
          <button type="button" className="wp-button" onClick={onSignOut} data-testid="sign-out">
            {t('common.signOut')}
          </button>
        </div>
      </div>
    </header>
  );
}
